/**
 * ============================================
 * App.tsx - Entry point principale dell'applicazione
 * ============================================
 * 
 * Questo file configura:
 * - Routing con React Router (code splitting per ottimizzazione)
 * - Provider per autenticazione, query, tooltip, SEO
 * - Lazy loading delle pagine per ridurre il bundle iniziale
 * 
 * Le pagine sono caricate dinamicamente tramite React.lazy()
 * per migliorare le performance di caricamento iniziale.
 */

import { Suspense, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { TenantSeoDefaults } from "@/components/seo/TenantSeoDefaults";
import WelcomeLNI from './pages/WelcomeLNI';

/**
 * CanonicalUrl
 * --------------------------------------------------------------------------
 * Inietta un <link rel="canonical"> self-referencing su ogni rotta basato
 * sulla pathname corrente. Le pagine che dichiarano un proprio canonical
 * tramite <Helmet> (es. Corsi, CorsoDettaglio) lo sovrascrivono grazie al
 * meccanismo di dedup di react-helmet-async.
 * Risolve il SEO finding "Pages are missing unique canonical links".
 */
const CanonicalUrl = () => {
  const { pathname } = useLocation();
  // SEO multi-tenant: il canonical è self-referencing rispetto al
  // dominio corrente (window.location.origin), così funziona per
  // qualsiasi tenant senza ancorare il portale al dominio LNI.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}${pathname}`;
  return (
    <Helmet>
      <link rel="canonical" href={url} />
    </Helmet>
  );
};
import { AuthProvider } from "@/hooks/useAuth";
import { AuthGuard } from "@/components/AuthGuard";
import { CookieBanner } from "@/components/CookieBanner";
import { TourProvider } from "@/contexts/TourContext";
import { BrandThemeProvider } from "@/components/BrandThemeProvider";
import { FeatureGuard } from "@/components/FeatureGuard";
import { toast } from "sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ContentProtectionGate } from "@/components/protection/ContentProtectionGate";
import { MoveUndoBanner } from "@/components/admin/MoveUndoBanner";
import { ContentImageViewer } from "@/components/content/ContentImageViewer";
import { BrokenAssetsAdminWatcher } from "@/components/admin/BrokenAssetsAdminWatcher";
import "@/styles/tour.css";
// Provider globale del sistema di ricerca contenuti (multi-scope).
import { SearchProvider } from "@/components/search/SearchProvider";
// Pannello debug navigazione (visibile solo con ?debug=nav o flag locale).
import { NavDebugPanel } from "@/components/debug/NavDebugPanel";

// ============================================
// Lazy Loading delle pagine
// ============================================

// Pagine pubbliche (caricamento immediato per la homepage)
import Index from "./pages/Index";

// Pagine autenticazione (lazy)
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Pagine utente autenticato (lazy)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardRipasso = lazy(() => import("./pages/DashboardRipasso"));
const Profile = lazy(() => import("./pages/Profile")); // Pagina profilo appena creata
const Corsi = lazy(() => import("./pages/Corsi"));
const CorsoDettaglio = lazy(() => import("./pages/CorsoDettaglio"));

// Pagine admin (lazy - caricate solo per amministratori)
const Admin = lazy(() => import("./pages/Admin"));
const AdminCorsi = lazy(() => import("./pages/AdminCorsi"));
const AdminCorsoDettaglio = lazy(() => import("./pages/AdminCorsoDettaglio"));
const AdminCorsoNuovo = lazy(() => import("./pages/AdminCorsoNuovo"));
const AdminSezioneDettaglio = lazy(() => import("./pages/AdminSezioneDettaglio"));
const AdminUtenti = lazy(() => import("./pages/AdminUtenti"));
const AdminAree = lazy(() => import("./pages/AdminAree"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDocumenti = lazy(() => import("./pages/AdminDocumenti"));
const AdminGlossario = lazy(() => import("./pages/AdminGlossario"));
const AdminTipografia = lazy(() => import("./pages/AdminTipografia"));
const AdminLinkUtili = lazy(() => import("./pages/AdminLinkUtili"));
const AdminBlocchiContenuto = lazy(() => import("./pages/AdminBlocchiContenuto"));
const AdminVersioni = lazy(() => import("./pages/AdminVersioni"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminVisibilitaContatti = lazy(() => import("./pages/AdminVisibilitaContatti"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const AdminEdizioni = lazy(() => import("./pages/AdminEdizioni"));
const AdminEdizioneNuova = lazy(() => import("./pages/AdminEdizioneNuova"));
const AdminEdizioneDettaglio = lazy(() => import("./pages/AdminEdizioneDettaglio"));
const AdminEdizioneModifica = lazy(() => import("./pages/AdminEdizioneModifica"));
const AdminAnagrafiche = lazy(() => import("./pages/AdminAnagrafiche"));
const AdminComunicazione = lazy(() => import("./pages/AdminComunicazione"));
const AdminPromemoria = lazy(() => import("./pages/AdminPromemoria"));
const AdminSegnalazioni = lazy(() => import("./pages/AdminSegnalazioni"));
const AdminDeveloperKeys = lazy(() => import("./pages/AdminDeveloperKeys"));
const AdminImpostazioni = lazy(() => import("./pages/AdminImpostazioni"));
const AdminVerifications = lazy(() => import("./pages/AdminVerifications"));
const AdminQuizMappings = lazy(() => import("./pages/AdminQuizMappings"));
const AdminDeltaAnalysis = lazy(() => import("./pages/AdminDeltaAnalysis"));
const AdminCarteggioExercises = lazy(() => import("./pages/AdminCarteggioExercises"));
const AdminTipiDocumento = lazy(() => import("./pages/AdminTipiDocumento"));
const AdminCredits = lazy(() => import("./pages/AdminCredits"));
const AdminExportDocs = lazy(() => import("./pages/AdminExportDocs"));
const AdminEsportaContenuti = lazy(() => import("./pages/AdminEsportaContenuti"));
const AdminValutazioni = lazy(() => import("./pages/AdminValutazioni"));
const AdminPreviewAccess = lazy(() => import("./pages/AdminPreviewAccess"));
const AdminProtezioneContenuti = lazy(() => import("./pages/AdminProtezioneContenuti"));
const AdminTenants = lazy(() => import("./pages/AdminTenants"));

// Pagine pubbliche secondarie (lazy)
const Candidature = lazy(() => import("./pages/Candidature"));
const Glossario = lazy(() => import("./pages/Glossario"));
const Bibliografia = lazy(() => import("./pages/Bibliografia"));
const LinkUtili = lazy(() => import("./pages/LinkUtili"));
const Forum = lazy(() => import("./pages/Forum"));
const ForumTopic = lazy(() => import("./pages/ForumTopic"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TerminiServizio = lazy(() => import("./pages/TerminiServizio"));
const Guida = lazy(() => import("./pages/Guida"));
const Presentazione = lazy(() => import("./pages/Presentazione"));
const PresentazionePortale = lazy(() => import("./pages/PresentazionePortale"));
const MembershipApply = lazy(() => import("./pages/MembershipApply"));
const MembershipApplySuccess = lazy(() => import("./pages/MembershipApplySuccess"));
const QuizMinisteriali = lazy(() => import("./pages/QuizMinisteriali"));
const Credits = lazy(() => import("./pages/Credits"));
const Nubi = lazy(() => import("./pages/Nubi"));
const NotFound = lazy(() => import("./pages/NotFound"));

// ============================================
// Configurazione Query Client
// ============================================
const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Caricamento...</div>
  </div>
);

function useGlobalErrorHandler() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("[GlobalErrorHandler] Unhandled promise rejection:", event.reason);
      event.preventDefault();
      
      const errorMessage = String(event.reason?.message || event.reason || "");
      const isAuthError = 
        errorMessage.includes("Refresh Token") ||
        errorMessage.includes("refresh_token") ||
        errorMessage.includes("Invalid Refresh Token") ||
        errorMessage.includes("JWT") ||
        errorMessage.includes("session");
      
      if (isAuthError) {
        console.info("[GlobalErrorHandler] Auth error detected, session will be cleared");
        return;
      }
      
      toast.error("Si è verificato un errore. Ricarica la pagina se il problema persiste.");
    };

    const handleError = (event: ErrorEvent) => {
      console.error("[GlobalErrorHandler] Uncaught error:", event.error);
      if (event.error?.name === "ChunkLoadError") {
        toast.error("Errore di caricamento. Ricarica la pagina.");
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);
}

function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  useGlobalErrorHandler();
  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <GlobalErrorHandler>
          <AuthProvider>
      <BrandThemeProvider>
            <TourProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <MoveUndoBanner />
                <ContentImageViewer />
                <BrokenAssetsAdminWatcher />
                <ContentProtectionGate />
                <BrowserRouter>
                  <ScrollToTop />
                  <CanonicalUrl />
                  <TenantSeoDefaults />
                  <SearchProvider>
                    <AuthGuard>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Homepage - caricamento immediato */}
                  <Route path="/" element={<Index />} />
                  
                  {/* Autenticazione */}
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/apply" element={
                    <FeatureGuard flag="feature_lni_workflow" fallback={<NotFound />}><MembershipApply /></FeatureGuard>
                  } />
                  <Route path="/apply/success" element={
                    <FeatureGuard flag="feature_lni_workflow" fallback={<NotFound />}><MembershipApplySuccess /></FeatureGuard>
                  } />
                  
                  {/* Dashboard utente e Profilo*/}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/dashboard/ripasso" element={<DashboardRipasso />} />
                  <Route path="/profilo" element={<Profile />} />
                  
                  {/* Corsi pubblici */}
                  <Route path="/corsi" element={<Corsi />} />
                  <Route path="/corso/:slugOrId" element={<CorsoDettaglio />} />
                  
                  {/* Area amministrazione */}
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/corsi" element={<AdminCorsi />} />
                  <Route path="/admin/corsi/nuovo" element={<AdminCorsoNuovo />} />
                  <Route path="/admin/corsi/:id" element={<AdminCorsoDettaglio />} />
                  <Route path="/admin/sezione/:id" element={<AdminSezioneDettaglio />} />
                  <Route path="/admin/utenti" element={<AdminUtenti />} />
                  <Route path="/admin/anteprime" element={<AdminPreviewAccess />} />
                  <Route path="/admin/aree" element={<AdminAree />} />
                  <Route path="/admin/documenti" element={<AdminDocumenti />} />
                  <Route path="/admin/glossario" element={<AdminGlossario />} />
                  <Route path="/admin/tipografia" element={<AdminTipografia />} />
                  <Route path="/admin/link-utili" element={<AdminLinkUtili />} />
                  <Route path="/admin/blocchi-contenuto" element={<AdminBlocchiContenuto />} />
                  <Route path="/admin/versioni" element={<AdminVersioni />} />
                  <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                  <Route path="/admin/email-logs" element={<AdminEmailLogs />} />
                  <Route path="/admin/edizioni" element={<AdminEdizioni />} />
                  <Route path="/admin/edizioni/nuova" element={<AdminEdizioneNuova />} />
                  <Route path="/admin/edizioni/:id" element={<AdminEdizioneDettaglio />} />
                  <Route path="/admin/edizioni/:id/modifica" element={<AdminEdizioneModifica />} />
                  <Route path="/admin/anagrafiche" element={
                    <FeatureGuard flag="feature_lni_workflow" fallback={<NotFound />}><AdminAnagrafiche /></FeatureGuard>
                  } />
                  <Route path="/admin/comunicazione" element={<AdminComunicazione />} />
                  <Route path="/admin/promemoria" element={<AdminPromemoria />} />
                  <Route path="/admin/segnalazioni" element={<AdminSegnalazioni />} />
                  <Route path="/admin/developer-keys" element={<AdminDeveloperKeys />} />
                  <Route path="/admin/impostazioni" element={<AdminImpostazioni />} />
                  <Route path="/admin/visibilita-contatti" element={<AdminVisibilitaContatti />} />
                  <Route path="/admin/protezione-contenuti" element={<AdminProtezioneContenuti />} />
                  <Route path="/admin/tenants" element={<AdminTenants />} />
                  <Route path="/admin/tipi-documento" element={<AdminTipiDocumento />} />
                  <Route path="/admin/verifications" element={
                    <FeatureGuard flag="feature_lni_workflow" fallback={<NotFound />}><AdminVerifications /></FeatureGuard>
                  } />
                  <Route path="/admin/quiz-mappings" element={
                    <FeatureGuard flag="feature_quiz_engine" fallback={<NotFound />}><AdminQuizMappings /></FeatureGuard>
                  } />
                  <Route path="/admin/delta-analysis" element={
                    <FeatureGuard flag="feature_quiz_engine" fallback={<NotFound />}><AdminDeltaAnalysis /></FeatureGuard>
                  } />
                  <Route path="/admin/carteggio-exercises" element={
                    <FeatureGuard flag="feature_quiz_engine" fallback={<NotFound />}><AdminCarteggioExercises /></FeatureGuard>
                  } />
                  <Route path="/admin/credits" element={
                    <FeatureGuard flag="feature_financials" fallback={<NotFound />}><AdminCredits /></FeatureGuard>
                  } />
                  <Route path="/admin/export-docs" element={<AdminExportDocs />} />
                  <Route path="/admin/esporta-contenuti" element={<AdminEsportaContenuti />} />
                  <Route path="/admin/valutazioni" element={<AdminValutazioni />} />
                  
                  {/* Candidature - solo per workflow LNI */}
                  <Route path="/candidature" element={
                    <FeatureGuard flag="feature_lni_workflow" fallback={<NotFound />}><Candidature /></FeatureGuard>
                  } />
                  
                  {/* Risorse */}
                  <Route path="/glossario" element={<Glossario />} />
                  <Route path="/bibliografia" element={<Bibliografia />} />
                  <Route path="/link-utili" element={<LinkUtili />} />
                  
                  {/* Forum */}
                  <Route path="/forum" element={<Forum />} />
                  <Route path="/forum/:topicId" element={<ForumTopic />} />
                  
                  {/* Pagine legali */}
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/termini" element={<TerminiServizio />} />
                  
                  {/* Guida */}
                  <Route path="/guida" element={<Guida />} />
                  <Route path="/guida/:categorySlug" element={<Guida />} />
                  <Route path="/guida/:categorySlug/:articleSlug" element={<Guida />} />
                  
                  {/* Presentazione */}
                  <Route path="/presentazione" element={<Presentazione />} />
                  <Route path="/presentazione-portale" element={<PresentazionePortale />} />
                  
                  {/* Quiz ministeriali */}
                  <Route path="/quiz" element={
                    <FeatureGuard flag="feature_quiz_engine" fallback={<NotFound />}>
                      <QuizMinisteriali />
                    </FeatureGuard>
                  } />
                  
                  {/* Crediti */}
                  <Route path="/credits" element={<Credits />} />
                  
                  {/* Visualizzatore didattico delle nubi per altitudine */}
                  <Route path="/nubi" element={<Nubi />} />

                  {/* Visualizzatore presentazione introduttiva portale */}
                  <Route path="/benvenuto" element={<WelcomeLNI />} />
                  
                  {/* Fallback per route non trovate */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
                    </AuthGuard>
                  </SearchProvider>
                  <CookieBanner />
                  <NavDebugPanel />
                </BrowserRouter>
              </TooltipProvider>
            </TourProvider>
      </BrandThemeProvider>
          </AuthProvider>
        </GlobalErrorHandler>
      </ErrorBoundary>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;